import React from "react";
import { motion } from "framer-motion";

const occasions = [
  "Weddings",
  "Anniversaries",
  "Milestones",
  "Baby Showers",
  "Births",
  "Bridal Showers",
  "Festivities",
  "Corporate",
  "Just because",
];

function OccasionsMarquee() {
  return (
    <div className="mt-20 md:mt-28 relative overflow-hidden border-y border-[#8B4513]/15 py-6 bg-[#fdfbf7]/60 backdrop-blur-sm">
      <div className="flex marquee whitespace-nowrap gap-12">
        {[...occasions, ...occasions, ...occasions].map((o, i) => (
          <span
            key={`${o}-${i}`}
            className="font-serif-display text-3xl md:text-5xl text-[#8B4513]/85 italic"
          >
            {o}
            <span className="mx-8 text-[#C4B0A2]">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function FoundersImage() {
  return (
    <div className="lg:col-span-5 relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9 }}
        className="relative overflow-hidden bg-[#fdfbf7] aspect-[4/5] shadow-[0_30px_60px_-30px_rgba(42,29,21,0.35)]"
      >
        <img
          src="/about-founders.jpg"
          alt="The founders of Gifting Concierge"
          className="w-full h-full object-cover"
        />

        <div className="absolute bottom-5 left-5 right-5 bg-[#fdfbf7]/85 backdrop-blur-sm px-5 py-3 flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-[#8B4513]" />
          <p className="text-[11px] uppercase tracking-[0.28em] text-[#2A1D15] font-medium">
            The Gifting Concierge Way
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: -15 }}
        whileInView={{ opacity: 1, scale: 1, rotate: -6 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="absolute -top-6 -right-4 sm:-top-8 sm:-right-8 w-28 h-28 md:w-36 md:h-36 rounded-full bg-[#8B4513] text-[#FDFBF7] flex items-center justify-center text-center shadow-[0_18px_40px_-18px_rgba(42,29,21,0.55)]"
        style={{ transform: "rotate(-6deg)" }}
      >
        <span className="font-serif-display italic text-lg md:text-2xl leading-[1.15]">
          Wrapped
          <br />
          with love
        </span>
      </motion.div>
    </div>
  );
}

function AboutCopy() {
  return (
    <div className="lg:col-span-7">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        data-testid="about-overline"
        className="text-[11px] uppercase tracking-[0.28em] text-[#8B4513] font-medium mb-8"
      >
        About Gifting Concierge
      </motion.p>

      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.1 }}
        className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#2A1D15]"
      >
        Redefining the art of{" "}
        <span className="italic text-[#8B4513]">gift-giving.</span>
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="mt-8 space-y-5 text-base md:text-lg text-[#5C4334] font-light leading-[1.85]"
      >
        <p>
          In a world where expressions of feelings and sentiments matter
          more than ever, we've joined forces to redefine the art of
          gift-giving. At our core, we are passionate about helping you
          ideate, curate and package the perfect gift for any occasion.
        </p>
        <p>
          We understand that gifting is a powerful way to convey that
          someone is truly special. Our mission is to elevate your gifting
          experience by making it thoughtful and unforgettable.
        </p>
      </motion.div>

      <motion.blockquote
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mt-10 border-l-2 border-[#8B4513] pl-6 max-w-2xl"
      >
        <p className="font-serif-display italic text-xl md:text-2xl text-[#8B4513] leading-snug">
          What sets us apart is our unique ability to turn your gifts into
          cherished memories.
        </p>
      </motion.blockquote>
    </div>
  );
}

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#f6ece1] overflow-hidden"
    >
      <div className="max-w-[1340px] mx-auto px-6 md:px-10 lg:px-16 xl:px-24 grid lg:grid-cols-12 gap-10 md:gap-14 lg:gap-20 items-center">
        <FoundersImage />
        <AboutCopy />
      </div>

      <OccasionsMarquee />
    </section>
  );
}
